import { Fragment, ReactNode } from 'react';
import { Link } from 'react-router-dom';

/**
 * Breadcrumbs — current-location trail above page content.
 *
 * Derived from M3 (no standalone Breadcrumb component) per Agent
 * Protocol step 2:
 *   - Spec:      M3 Navigation guidance implies breadcrumbs via the
 *                Top App Bar headline + supporting text pattern; no
 *                explicit component. Composed pattern.
 *   - Reference: github.com/material-components/material-web (none)
 *
 * Populus adaptations applied:
 *   - Text + chevron separator (›) — matches business-app conventions
 *     (vs M3 implied dropdown menus for deep hierarchies)
 *   - Last segment in --color-ink + medium weight (current location;
 *     not a link)
 *   - Earlier segments in --color-ink-3 + link styling; hover brand
 *     accent on link items
 *   - First segment optional (e.g., "Clients › Alliance Property Tax
 *     › 758 Kirby Dr Retail" — three levels deep)
 *
 * Token discipline:
 *   - All text/separator → --color-ink* (system; never per-org)
 *   - Hover accent on links → --brand-primary (org-overridable per
 *     design-system.md § Color — System vs Brand tokens)
 *
 * Phase 11 Block 6 PR 4 (2026-05-12).
 */

export interface BreadcrumbItem {
  label: ReactNode;
  to?: string;        // undefined → current page (non-link)
}

export interface BreadcrumbsProps {
  items: BreadcrumbItem[];
}

export function Breadcrumbs({ items }: BreadcrumbsProps) {
  return (
    <nav aria-label="Breadcrumb" style={{
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--sp-2)',
      fontSize: 'var(--fs-sm)',
      color: 'var(--color-ink-3)',
    }}>
      {items.map((item, i) => {
        const isLast = i === items.length - 1;
        const sep = (
          <span aria-hidden="true" style={{
            color: 'var(--color-muted)',
            margin: '0 var(--sp-1)',
          }}>›</span>
        );
        const content = item.to && !isLast ? (
          <Link
            to={item.to}
            style={{
              color: 'var(--color-ink-3)',
              textDecoration: 'none',
              fontWeight: 'var(--fw-regular)',
            }}
          >
            {item.label}
          </Link>
        ) : (
          <span style={{
            color: isLast ? 'var(--color-ink)' : 'var(--color-ink-3)',
            fontWeight: isLast ? 'var(--fw-medium)' : 'var(--fw-regular)',
          }} aria-current={isLast ? 'page' : undefined}>
            {item.label}
          </span>
        );
        return (
          <Fragment key={i}>
            {i > 0 && sep}
            {content}
          </Fragment>
        );
      })}
    </nav>
  );
}
